/**
 * Protected Route Component
 *
 * Wraps routes that require authentication. If the user is not authenticated,
 * stores the current URL (path + query + hash) as returnTo and redirects
 * to the sign-in page.
 *
 * Business Rules:
 * - Preserves deep links: when user clicks email link like /scheduled-items?edit=19
 *   or Overview nurture links like /overview?eventType=EVT-005#send-a-smile,
 *   we store that URL and return to it after successful auth + terms acceptance.
 * - Uses /auth/signin instead of /login for direct MSAL redirect (no extra click).
 */

import { Navigate, useLocation } from 'react-router-dom';
import { Loader, Center } from '@mantine/core';
import { useAuth } from '../contexts/AuthContext';
import { AUTH_ROUTES, sanitizeReturnToPath, setReturnTo } from '../constants/auth';
import { semanticColors } from '../config/theme';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Center style={{ height: 'var(--app-vh, 100vh)', background: semanticColors.bg.warmGradient }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!isAuthenticated) {
    // Store path + query + hash so Overview seasonal deep links survive login.
    const returnTo = location.pathname + location.search + location.hash;
    if (sanitizeReturnToPath(returnTo)) {
      setReturnTo(returnTo);
    }

    // Redirect to sign-in (which immediately triggers MSAL redirect)
    return <Navigate to={AUTH_ROUTES.SIGNIN} replace />;
  }

  return <>{children}</>;
};



